<template>
    <Modal :isVisible="isVisible" @close="closeModal">
        <div class="modal-header-v2 border-bottom pb-3 mb-4">
            <div>
                <h3>{{ listaDeEquipamentos?.name || 'Equipamento' }}</h3>
                <span class="text-muted">Selecione componentes internos pertencentes ou visíveis ao perfil pai.</span>
            </div>
            <button class="close-btn" @click="closeModal"><i class="bi bi-x-lg"></i></button>
        </div>

        <div class="modal-body-v2">
            <template v-if="perfilEditavel.length > 0">
                <div class="d-flex flex-column gap-3">
                    <div 
                        v-for="(item, index) in perfilEditavel" 
                        :key="item.equipId" 
                        class="modern-list-item align-items-center"
                        :class="item.status ? 'active-selection' : ''"
                    >
                        <!-- Custom V2 Checkbox & Input -->
                        <div class="flex-grow-1 row m-0 p-0 align-items-center">
                            <div class="col-md-3 col-sm-4 px-1">
                                <label class="custom-checkbox-wrapper">
                                    <input type="checkbox" v-model="item.status" class="modern-checkbox" />
                                    <span class="custom-check-icon"></span>
                                    <span class="check-label text-muted ms-2">{{ item.equipId }}</span>
                                </label>
                            </div>
                            <div class="col-md-9 col-sm-8 px-1">
                                <div class="input-wrapper disabled-wrap">
                                    <i class="bi bi-tag-fill icon text-primary"></i>
                                    <input type="text" class="form-control disabled-input" v-model="item.name" disabled />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <template v-else>
                <div class="empty-state-v2">
                    <i class="bi bi-ban empty-icon text-muted"></i>
                    <p class="mt-2">Nenhum equipamento derivado mapeado na API.</p>
                </div>
            </template>
        </div>

        <div class="modal-footer-v2 mt-4 pt-3 border-top">
            <button class="btn-cancel" @click="closeModal">Cancelar</button>
            <BotaoSalvar @click="saveNewEquipement" />
        </div>
    </Modal>
</template>

<script setup>
import { ref, watch } from 'vue';
import Modal from '@/components/Modal.vue';
import BotaoSalvar from './ui/BotaoSalvar.vue';
import { showToast } from '@/eventBus.js';
import axios from '@/axios.js';

// Props
const props = defineProps({
    isVisible: { type: Boolean, required: true },
    listaDeEquipamentos: { type: Object, required: true }
});

// Emits
const emit = defineEmits(['close']);

const perfilEditavel = ref([]);

watch(
    () => props.isVisible,
    async (newVal) => {
        if (newVal) {
            await loadEquipments();
        } else {
            clearData();
        }
    }
);

async function loadEquipments() {
    const companyId = localStorage.getItem("idCompany");
    axios.post(`/api/equipments/others/${companyId}`, props.listaDeEquipamentos)
        .then(response => { perfilEditavel.value = response.data; })
        .catch(error => { showToast(error?.response?.data?.message) });
}

function closeModal() {
    emit('close');
    clearData();
}

function clearData() {
    perfilEditavel.value = [];
}

async function saveNewEquipement() {
    const companyId = localStorage.getItem("idCompany");
    const selectedEquipments = perfilEditavel.value
        .filter(equip => equip.status)
        .map(equip => ({
            equipId: equip.equipId,
            name: equip.name,
            image: equip.image,
            status: equip.status
        }));

    const enviarDados = {
        equipments: selectedEquipments,
        type: props.listaDeEquipamentos.type,
        companyId,
        _id: props.listaDeEquipamentos._id,
        name: props.listaDeEquipamentos.name
    };

    axios.put(`/api/profile-value/${companyId}`, enviarDados)
        .then(response => {
            showToast(response.data.message);
            window.location.reload();
        })
        .catch(error => {
            showToast(error?.response?.data?.message)
        });
}
</script>

<style scoped lang="scss">

/* Modals Override Settings V2 */
.modal-header-v2 {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.modal-header-v2 h3 {
  margin: 0 0 0.25rem 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #0F172A;
}

.text-muted { color: #64748B !important; }

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.2rem;
  color: #94A3B8;
  cursor: pointer;
  transition: color 0.2s;
}
.close-btn:hover { color: #EF4444; }

.modal-body-v2 {
    max-height: 50vh;
    overflow-y: auto;
    padding: 0.5rem 0;
}

.modal-footer-v2 {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
}

.btn-cancel {
  background: transparent;
  border: none;
  font-weight: 600;
  color: #64748B;
  cursor: pointer;
  padding: 0.5rem 1rem;
  transition: color 0.2s;
}
.btn-cancel:hover { color: #0F172A; }

/* Item List styles */
.modern-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #F8FAFC;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid #E2E8F0;
  transition: border-color 0.2s, background-color 0.2s;
}
.modern-list-item:hover { border-color: #CBD5E1; }
.modern-list-item.active-selection {
    background-color: #EFF6FF;
    border-color: #3B82F6;
}

/* Custom Checkbox V2 */
.custom-checkbox-wrapper {
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
}
.modern-checkbox {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}
.custom-check-icon {
  height: 22px;
  width: 22px;
  background-color: #FFFFFF;
  border: 2px solid #CBD5E1;
  border-radius: 6px;
  display: inline-block;
  position: relative;
  transition: all 0.2s ease;
}
.modern-checkbox:checked ~ .custom-check-icon {
  background-color: #DF523A;
  border-color: #DF523A;
}
.custom-check-icon:after {
  content: "";
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 12px;
  border: solid white;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
}
.modern-checkbox:checked ~ .custom-check-icon:after { display: block; }
.check-label {
    font-weight: 600;
    font-size: 0.95rem;
}

/* Inputs wrapper Disabled */
.input-wrapper.disabled-wrap {
    display: flex;
    align-items: center;
    background-color: #E2E8F0;
    border: 1px solid #CBD5E1;
    border-radius: 8px;
    padding: 0.35rem 0.8rem;
}
.input-wrapper.disabled-wrap .icon { margin-right: 0.75rem; color: #64748B !important; }
.form-control.disabled-input {
    background: transparent;
    border: none;
    font-size: 0.9rem;
    font-weight: 600;
    color: #475569;
    padding: 0;
}

.empty-state-v2 {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2.5rem;
    text-align: center;
}
.empty-state-v2 .empty-icon {
    font-size: 2.5rem;
    opacity: 0.5;
}
</style>
